import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../../hooks';
import { safeArray } from '../../utils/helpers';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { CACHE_TTL } from '../../constants';

const ChamaList = () => {
  const { data: chamas, loading } = useFetch('/chamas', {
    immediate: true,
    defaultValue: [],
    cacheTtl: CACHE_TTL.SHORT,
    transform: (response) => safeArray(response),
  });

  const [filteredChamas, setFilteredChamas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filterChamas = () => {
    let filtered = Array.isArray(chamas) ? [...chamas] : [];
    if (searchQuery) {
      filtered = filtered.filter(
        (chama) =>
          chama.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (chama.description && chama.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter((chama) => chama.status === filterStatus);
    }
    setFilteredChamas(filtered);
  };

  useEffect(() => {
    filterChamas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterStatus, chamas]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">My Chamas</h1>
          <p className="mt-1 text-[#d8d2c5]">Manage your savings groups and communities</p>
        </div>
        <Link to="/chamas/create">
          <Button
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            }
          >
            Create Chama
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search chamas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/5 text-[#d8d2c5] hover:bg-white/10 border border-white/10'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/5 text-[#d8d2c5] hover:bg-white/10 border border-white/10'
              }`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'inactive'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/5 text-[#d8d2c5] hover:bg-white/10 border border-white/10'
              }`}
            >
              Inactive
            </button>
          </div>
        </div>
      </Card>

      {/* Chamas Grid */}
      {filteredChamas.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg
              className="w-20 h-20 mx-auto text-white/10 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="text-xl font-semibold text-white mb-2">
              {searchQuery || filterStatus !== 'all' ? 'No chamas found' : 'No chamas yet'}
            </p>
            <p className="text-[#d8d2c5] mb-6">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first chama to get started'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <Link to="/chamas/create">
                <Button>Create Your First Chama</Button>
              </Link>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredChamas.map((chama) => (
            <Link key={chama.id} to={`/chamas/${chama.id}`}>
              <Card padding={false} hover className="h-full">
                <div className="p-6">
                  {/* Avatar & Status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">
                        {chama.name?.charAt(0) || 'C'}
                      </span>
                    </div>
                    <Badge variant={chama.status === 'active' ? 'success' : 'neutral'}>
                      {chama.status}
                    </Badge>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-white mb-2">{chama.name}</h3>
                  <p className="text-sm text-[#d8d2c5] mb-4 line-clamp-2">
                    {chama.description || 'No description available'}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-xs text-[#d8d2c5]">Members</p>
                      <p className="text-lg font-bold text-white">{chama.members_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#d8d2c5]">Contributions</p>
                      <p className="text-lg font-bold text-white">
                        KES {(chama.total_contributions || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Color Bar */}
                <div className="h-1.5 bg-gradient-to-r from-emerald-400 to-sky-500" />
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Stats Summary */}
      {filteredChamas.length > 0 && (
        <Card>
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="text-sm text-gray-500">Total Chamas</p>
              <p className="text-2xl font-bold text-gray-900">{filteredChamas.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredChamas.reduce((sum, chama) => sum + (chama.members_count || 0), 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Contributions</p>
              <p className="text-2xl font-bold text-gray-900">
                KES{' '}
                {filteredChamas
                  .reduce((sum, chama) => sum + (chama.total_contributions || 0), 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ChamaList;
